import {
  rebindPuppyLinearComponentPatternBodyOccurrences,
  remapPuppyLinearComponentPatternNodeReferences,
} from '../../export/puppy/puppyLinearComponentPatternExtension'
import { ProjectModelLinearComponentPatternComponentRootRebinder } from './ProjectModelLinearComponentPatternComponentRootRebinder'
import { rebindProjectModelMateDocumentToImportedResultRuntime } from './rebind-project-model-imported-result-runtime'
import { resolveProjectModelBodyOccurrenceRebinding } from './utils/resolve-project-model-body-occurrence-rebinding'
import {
  areProjectModelRuntimeNodeIdsEquivalent,
  normalizeProjectModelRuntimeNodeId,
} from './project-model-node-identity'

/**
 * 项目模型局部子树的装配关系最终化器。
 *
 * 该模块只消费已经冻结的 hierarchy、package alias 与 body occurrence 映射，统一完成
 * Mate 文档和线性零部件阵列文档的最终重绑定；它不修改场景、资源 owner 或运行时状态。
 */
export class ProjectModelSubtreeRelationFinalizer {
  /**
   * 把当前子树携带的 Mate 文档重绑到最终 occurrence 与 body。
   *
   * @param mateDocuments 待恢复的根/子装配 Mate 文档。
   * @param context hierarchy、包作用域与 body 重绑定上下文。
   * @returns 可直接交给 Mate Controller 恢复的文档。
   */
  public finalizeMateDocuments(
    mateDocuments: readonly PuppyCAD.ProjectModelSubtreeSemanticMateDocument[],
    context: PuppyCAD.ProjectModelSubtreeRelationFinalizationContext,
  ): readonly PuppyCAD.ProjectModelSubtreeSemanticMateDocument[] {
    return mateDocuments.map((mateDocument) => {
      const rebindingContext = this.resolveMateDocumentRebindingContext(
        mateDocument,
        context,
      )
      return {
        ...mateDocument,
        document: rebindProjectModelMateDocumentToImportedResultRuntime({
          document: mateDocument.document,
          generatedHierarchy: rebindingContext.generatedHierarchy,
          rebasedHierarchy: context.finalizedHierarchy,
          bodyOccurrenceRebindings: context.bodyOccurrenceRebindings,
          documentAssemblyNodeId: mateDocument.assemblyNodeId,
          explicitNodeAliases: rebindingContext.explicitNodeAliases,
        }),
      }
    })
  }

  /**
   * 最终化当前子树内全部线性零部件阵列文档。
   *
   * @param patternDocuments 待恢复的阵列文档。
   * @param context hierarchy、包作用域与 body 重绑定上下文。
   * @returns owner、普通引用、拓扑引用和组件根均已闭合的阵列文档。
   */
  public finalizeLinearComponentPatternDocuments(
    patternDocuments: readonly PuppyCAD.ProjectModelSubtreeSemanticLinearComponentPatternDocument[],
    context: PuppyCAD.ProjectModelSubtreeRelationFinalizationContext,
  ): readonly PuppyCAD.ProjectModelSubtreeSemanticLinearComponentPatternDocument[] {
    return patternDocuments.map((patternDocument) => (
      this.finalizeLinearComponentPatternDocument(patternDocument, context)
    ))
  }

  /** 解析单份 Mate 文档可见的唯一 package alias 作用域。 */
  private resolveMateDocumentRebindingContext(
    mateDocument: PuppyCAD.ProjectModelSubtreeSemanticMateDocument,
    context: PuppyCAD.ProjectModelSubtreeRelationFinalizationContext,
  ) {
    if (mateDocument.restoreAsRootDocument) {
      return {
        generatedHierarchy: context.generatedHierarchy,
        explicitNodeAliases: context.explicitNodeAliases,
      }
    }

    const ownerAssemblyNodeId = normalizeProjectModelRuntimeNodeId(mateDocument.assemblyNodeId)
    const matchingScopes = context.packageSemanticScopes.filter((scope) => (
      areProjectModelRuntimeNodeIdsEquivalent(scope.ownerAssemblyNodeId, ownerAssemblyNodeId)
    ))
    if (matchingScopes.length === 0) {
      return {
        generatedHierarchy: context.generatedHierarchy,
        explicitNodeAliases: context.explicitNodeAliases,
      }
    }
    if (matchingScopes.length > 1) {
      throw new Error(
        '项目模型局部替换 scoped Mate 存在多个 package owner：'
        + `assemblyNodeId=${ownerAssemblyNodeId}, candidates=${matchingScopes.length}`,
      )
    }
    const matchingScope = matchingScopes[0]
    if (!matchingScope) {
      throw new Error(`项目模型局部替换 scoped Mate package owner 不可用：${ownerAssemblyNodeId}`)
    }

    const explicitNodeAliases = new Map(context.explicitNodeAliases)
    const packageAliases = context.aliasByOwnerAssemblyNodeId.get(ownerAssemblyNodeId) ?? {}
    Object.entries(packageAliases).forEach(([sourceNodeId, targetNodeId]) => {
      const currentTargetNodeId = explicitNodeAliases.get(sourceNodeId)
      if (currentTargetNodeId
        && !areProjectModelRuntimeNodeIdsEquivalent(currentTargetNodeId, targetNodeId)) {
        throw new Error(
          `项目模型局部替换 scoped Mate 显式身份冲突：${sourceNodeId} -> ${currentTargetNodeId} / ${targetNodeId}`,
        )
      }
      explicitNodeAliases.set(sourceNodeId, targetNodeId)
    })

    return {
      generatedHierarchy: matchingScope.sourceHierarchy,
      explicitNodeAliases,
    }
  }

  /** 使用 package alias 与 body occurrence 映射最终化单份线性阵列文档。 */
  private finalizeLinearComponentPatternDocument(
    patternDocument: PuppyCAD.ProjectModelSubtreeSemanticLinearComponentPatternDocument,
    context: PuppyCAD.ProjectModelSubtreeRelationFinalizationContext,
  ): PuppyCAD.ProjectModelSubtreeSemanticLinearComponentPatternDocument {
    const ownerAssemblyNodeId = normalizeProjectModelRuntimeNodeId(patternDocument.ownerAssemblyNodeId)
    const matchingScopes = context.packageSemanticScopes.filter(scope => (
      areProjectModelRuntimeNodeIdsEquivalent(scope.ownerAssemblyNodeId, ownerAssemblyNodeId)
    ))
    if (matchingScopes.length !== 1) {
      throw new Error(
        '项目模型局部替换线性阵列 package owner 无法唯一定位：'
        + `ownerAssemblyNodeId=${ownerAssemblyNodeId}, candidates=${matchingScopes.length}`,
      )
    }
    const matchingScope = matchingScopes[0]
    if (!matchingScope) {
      throw new Error(`项目模型局部替换线性阵列 package owner 不可用：${ownerAssemblyNodeId}`)
    }
    const finalOwnerAssemblyNodeId = normalizeProjectModelRuntimeNodeId(
      matchingScope.ownerAssemblyNodeId,
    )
    const aliasByNodeId: Record<string, string> = {}
    context.explicitNodeAliases.forEach((targetNodeId, sourceNodeId) => {
      aliasByNodeId[sourceNodeId] = targetNodeId
    })
    const packageAliases = context.aliasByOwnerAssemblyNodeId.get(finalOwnerAssemblyNodeId) ?? {}
    Object.entries(packageAliases).forEach(([sourceNodeId, targetNodeId]) => {
      const currentTargetNodeId = aliasByNodeId[sourceNodeId]
      if (currentTargetNodeId
        && !areProjectModelRuntimeNodeIdsEquivalent(currentTargetNodeId, targetNodeId)) {
        throw new Error(
          '项目模型局部替换线性阵列 package alias 冲突：'
          + `${sourceNodeId} -> ${currentTargetNodeId} / ${targetNodeId}`,
        )
      }
      aliasByNodeId[sourceNodeId] = targetNodeId
    })

    const remappedDocument = remapPuppyLinearComponentPatternNodeReferences({
      document: patternDocument.document,
      aliasByNodeId,
    })
    const ownedDocument = {
      ...remappedDocument,
      records: remappedDocument.records.map(record => ({
        ...record,
        ownerAssemblyNodeId: finalOwnerAssemblyNodeId,
      })),
    }
    const topologyReboundDocument = rebindPuppyLinearComponentPatternBodyOccurrences({
      document: ownedDocument,
      resolveBodyOccurrence: ({ featureId, reference }) => {
        const occurrenceScopedRebindings = context.bodyOccurrenceRebindings.filter(rebinding => (
          areProjectModelRuntimeNodeIdsEquivalent(
            rebinding.targetAssemblyNodeId,
            reference.assemblyNodeId,
          )
        ))
        const rebinding = resolveProjectModelBodyOccurrenceRebinding(
          reference.bodyBindingId,
          occurrenceScopedRebindings,
        )
        if (!rebinding) {
          throw new Error(
            '项目模型局部替换线性阵列 imported BRep 引用未知 body：'
            + `owner=${finalOwnerAssemblyNodeId}, featureId=${featureId}, `
            + `bodyBindingId=${reference.bodyBindingId}`,
          )
        }
        return {
          ...reference,
          assemblyNodeId: rebinding.targetAssemblyNodeId,
          bodyBindingId: rebinding.targetBodyBindingId,
        }
      },
    })
    const document = new ProjectModelLinearComponentPatternComponentRootRebinder().rebind({
      document: topologyReboundDocument,
      importedResult: context.importedResult,
      ownerAssemblyNodeId: finalOwnerAssemblyNodeId,
    })
    return { ownerAssemblyNodeId: finalOwnerAssemblyNodeId, document }
  }
}
